/**
 * シフト変更のお知らせ(2026-09-22 発注者回答。P-45 → D-130)。
 *
 * 「提出されたものを正として確定に。提出された段階で修正があれば管理者が変更 → アポインターに通知。
 *   提出後の修正は現在のフロー(変更申請 → 承認)と同様に」
 *  - 管理者が **提出済みのシフトの時間を直したら**(シフト表のセル・シフト提出の代理入力)、本人向けのお知らせを作る
 *  - 1人1か月 = `shift_notices/{month}_{op_id}`。**同じ日は1件にまとめる**(何度直しても「最初の変更前 → いま」の1件。
 *    最初の変更前と同じ時間に戻したら消える)
 *  - 本人が「確認しました」を押すと消える(変更の履歴はシフト提出の logs に残っている)
 *  - **欠勤・当欠の記録だけの変更は知らせない**(本人の連絡を受けて付ける記録のため)
 *  - 変更申請を承認したときは、その日のお知らせを消す(本人が出した変更のため。承認の結果は変更申請の画面で見える)
 */
import { FieldPath, collection, deleteField, doc, documentId, getDoc, getDocs, orderBy, query, serverTimestamp, where, writeBatch, type WriteBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { WORK_STYLE_LABELS, type MonthStr, type Operator } from '@core/lib/types';
import { breakMinOf, dayHours, gapHoursOf, gapLabel, normalizeDays, submissionData, submissionId, type SaveSubmissionArgs, type SubmitDay } from './shiftSubmitRepo';

/** お知らせに載せる、その日のシフト。null = 休み */
export type NoticeSlot = {
  start: number;
  end: number;
  break_min: number;
  gap_hours?: number;
  /** 中抜けの開始・終了(2026-09-25) */
  gap_from?: number;
  gap_to?: number;
  status?: 'absent' | 'same_day';
  /** 出勤形態の表示名(出勤・在宅など)。変わったときだけ画面に出す */
  style?: string;
} | null;

export type ShiftNoticeItem = {
  /** 日(2桁。'05') */
  day: string;
  /** 最初に直す前(本人が出したもの) */
  before: NoticeSlot;
  /** いま */
  after: NoticeSlot;
  before_hours: number;
  after_hours: number;
  /** 最後に直した人 */
  by: string;
  at: string;
};

export type ShiftNotice = {
  id: string;
  month: MonthStr;
  op_id: string;
  name: string;
  items: Record<string, ShiftNoticeItem>;
  read_at?: string;
  read_by?: string;
};

export type NoticeChange = { day: string; before: NoticeSlot; after: NoticeSlot; by: string; at: string };

// ---- 計算(画面・保存から使う。テストあり) ---------------------------------------------

/** BB ID → 出勤形態の表示名 */
export function styleResolver(operators: Pick<Operator, 'bb_accounts'>[]): (bbId: string | undefined) => string | undefined {
  const map = new Map<string, string>();
  for (const o of operators) for (const a of o.bb_accounts ?? []) map.set(a.bb_id, WORK_STYLE_LABELS[a.work_style]);
  return (bbId) => (bbId ? map.get(bbId) : undefined);
}

/** シフト提出の1日 → お知らせの形(休憩は未入力なら既定値) */
export function slotOf(d: SubmitDay | undefined, styleOf?: (bbId: string | undefined) => string | undefined): NoticeSlot {
  if (!d) return null;
  const s: NonNullable<NoticeSlot> = { start: d.start, end: d.end, break_min: breakMinOf(d) };
  if (gapHoursOf(d)) s.gap_hours = gapHoursOf(d);
  if (d.gap_from !== undefined) s.gap_from = d.gap_from;
  if (d.gap_to !== undefined) s.gap_to = d.gap_to;
  if (d.status) s.status = d.status;
  const style = styleOf?.(d.bb_id);
  if (style) s.style = style;
  return s;
}

/** その日の時間(休憩・中抜けを引いた後。欠勤・休みは0) */
export function slotHours(s: NoticeSlot): number {
  return s ? dayHours({ start: s.start, end: s.end, break_min: s.break_min, gap_hours: s.gap_hours, status: s.status }) : 0;
}

const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

/** 「9:00〜18:00(休憩60分)」「休み」「欠勤」。withStyle で先頭に出勤形態 */
export function slotLabel(s: NoticeSlot, withStyle = false): string {
  if (!s) return '休み';
  if (s.status === 'absent') return '欠勤';
  if (s.status === 'same_day') return '当日欠勤';
  const extra = [s.break_min ? `休憩${s.break_min}分` : '', s.gap_hours ? `中抜け${gapLabel(s)}` : ''].filter(Boolean).join('・');
  return `${withStyle && s.style ? `${s.style} ` : ''}${hhmm(s.start)}〜${hhmm(s.end)}${extra ? `(${extra})` : ''}`;
}

/** 時間が変わったか(開始・終了・休憩・中抜け・出勤形態・休み)。欠勤・当欠の記録だけの変更は含めない */
const timeKey = (s: NoticeSlot) => (s ? `${s.style ?? ''}|${s.start}-${s.end}|${s.break_min}|${s.gap_hours ?? 0}` : 'off');
export function isTimeChange(before: NoticeSlot, after: NoticeSlot): boolean {
  return timeKey(before) !== timeKey(after);
}

/** 2つの日ごとのシフトで中身が違う日(開始・終了・休憩・中抜け・欠勤・休み・出勤形態) */
export function changedDaysOf(before: Record<string, SubmitDay>, after: Record<string, SubmitDay>): string[] {
  const key = (d: SubmitDay | undefined) => (d ? JSON.stringify([d.bb_id ?? '', d.start, d.end, breakMinOf(d), d.gap_hours ?? 0, d.status ?? '']) : '');
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((k) => key(before[k]) !== key(after[k])).sort();
}

/**
 * いまのお知らせに今回の変更を足す(同じ日は1件にまとめる)。
 *  - お知らせがまだ無い日: 時間が変わったときだけ作る(欠勤・当欠の記録だけなら作らない)
 *  - もうある日(本人が未確認): 「最初の変更前」はそのまま「いま」を差し替える。最初の変更前と同じ時間に戻ったら消す
 */
export function mergeNoticeItems(existing: Record<string, ShiftNoticeItem>, changes: NoticeChange[]): Record<string, ShiftNoticeItem> {
  const out: Record<string, ShiftNoticeItem> = { ...existing };
  for (const c of changes) {
    const prev = out[c.day];
    if (!prev && !isTimeChange(c.before, c.after)) continue;
    const before = prev ? prev.before : c.before;
    if (!isTimeChange(before, c.after)) {
      delete out[c.day];
      continue;
    }
    out[c.day] = { day: c.day, before, after: c.after, before_hours: slotHours(before), after_hours: slotHours(c.after), by: c.by, at: c.at };
  }
  return out;
}

// ---- 読み書き -------------------------------------------------------------------------

const toNotice = (id: string, v: Record<string, unknown>): ShiftNotice => ({
  id,
  month: v.month as MonthStr,
  op_id: String(v.op_id ?? ''),
  name: String(v.name ?? ''),
  items: (v.items as Record<string, ShiftNoticeItem> | undefined) ?? {},
  read_at: v.read_at as string | undefined,
  read_by: v.read_by as string | undefined,
});

/** 本人のお知らせ(まだ確認していないものだけ。月の古い順) */
export async function fetchMyNotices(opId: string): Promise<ShiftNotice[]> {
  const snap = await getDocs(query(collection(getDb(), 'shift_notices'), where('op_id', '==', opId)));
  return snap.docs
    .map((d) => toNotice(d.id, d.data()))
    .filter((n) => Object.keys(n.items).length > 0)
    .sort((a, b) => a.month.localeCompare(b.month));
}

/** その月の全員分(管理者: シフト表で「本人未確認」を出す) */
export async function fetchMonthNotices(month: MonthStr): Promise<ShiftNotice[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'shift_notices'), where(documentId(), '>=', `${month}_`), where(documentId(), '<=', `${month}_`), orderBy(documentId())),
  );
  return snap.docs.map((d) => toNotice(d.id, d.data()));
}

export type AdminSaveArgs = SaveSubmissionArgs & {
  /** 直す前の日ごとのシフト(保存済みのもの) */
  prevDays: Record<string, SubmitDay>;
  /** お知らせに出す「直した人」 */
  byName: string;
  /** 本人に知らせない(管理者が自分のシフトを直したとき) */
  silent?: boolean;
  styleOf?: (bbId: string | undefined) => string | undefined;
};

/**
 * 管理者が提出済みのシフトを直して保存する。**シフト提出とお知らせを1回で書く**(途中で失敗したら何も残さない)。
 * 返り値 = 保存後にお知らせに載っている日(本人が未確認のもの)
 */
export async function saveSubmissionByAdmin(args: AdminSaveArgs): Promise<string[]> {
  const db = getDb();
  const id = submissionId(args.month, args.opId);
  const next = normalizeDays(args.days);
  const at = new Date().toISOString();
  const changes: NoticeChange[] = changedDaysOf(args.prevDays, next).map((day) => ({
    day,
    before: slotOf(args.prevDays[day], args.styleOf),
    after: slotOf(next[day], args.styleOf),
    by: args.byName,
    at,
  }));
  const batch = writeBatch(db);
  batch.set(doc(db, 'shift_submissions', id), submissionData(args));
  let items: Record<string, ShiftNoticeItem> = {};
  if (!args.silent && changes.length > 0) {
    const snap = await getDoc(doc(db, 'shift_notices', id));
    const existing = snap.exists() ? toNotice(id, snap.data()).items : {};
    items = mergeNoticeItems(existing, changes);
    if (JSON.stringify(items) !== JSON.stringify(existing)) {
      batch.set(doc(db, 'shift_notices', id), { month: args.month, op_id: args.opId, name: args.name, items, updated_by: args.userId, updated_at: serverTimestamp() });
    }
  }
  await batch.commit();
  return Object.keys(items).sort();
}

/** 変更申請を承認したとき、その日のお知らせを消す(同じ batch で書く) */
export async function clearNoticeDay(batch: WriteBatch, month: MonthStr, opId: string, day: string): Promise<void> {
  const ref = doc(getDb(), 'shift_notices', submissionId(month, opId));
  const snap = await getDoc(ref);
  if (snap.exists() && toNotice(ref.id, snap.data()).items[day]) batch.update(ref, new FieldPath('items', day), deleteField());
}

/** 本人が「確認しました」を押したとき。お知らせを空にする(変更の履歴はシフト提出の logs に残っている) */
export async function acknowledgeNotices(notices: Pick<ShiftNotice, 'id'>[], userId: string): Promise<void> {
  const db = getDb();
  const batch = writeBatch(db);
  const at = new Date().toISOString();
  for (const n of notices) batch.update(doc(db, 'shift_notices', n.id), { items: {}, read_at: at, read_by: userId });
  await batch.commit();
}
